import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Session } from './auth/authApi'
import { signedInAs } from './auth/authDisplay'
import { formatClinicalDateTime } from './format/clinicalDate'
import { LanguageChoice } from './i18n/LanguageChoice'
import { DeploymentNotice } from './DeploymentNotice'
import { TranslationNotice } from './i18n/TranslationNotice'
import { Mark } from './Mark'
import { PatientOverviewScreen } from './patient/PatientOverview'
import { CheckpointReminder } from './ward/CheckpointReminder'
import { PatientRegistrationScreen } from './patient/PatientRegistration'
import { PatientSearchScreen } from './patient/PatientSearch'
import type { PrintDocument } from './print/printApi'
import { CheckpointScreen } from './print/CheckpointScreen'
import { PrintScreen } from './print/PrintScreen'
import { WardListScreen } from './ward/WardList'

type ApiStatus = 'checking' | 'ready' | 'alive-no-database' | 'unreachable'

/**
 * Which screen is showing.
 *
 * Still a piece of state rather than a router. Five screens, no deep links to support
 * and no URLs printed on the A4 sheet other than the host itself — a routing dependency
 * would be a dependency to justify under AGENTS.md §8 for something `useState` does. The
 * day the note flow needs a per-patient address (phase 0 day 9, PHASE-0.md §2.5) is the
 * day to ask for one.
 *
 * A discriminated union rather than a string and a nullable id beside it, because two
 * independent pieces of state can hold a combination that means nothing — `'ward'` with
 * a patient id set, or `'patient'` with none — and the second of those is a blank screen
 * with no error on it.
 *
 * **`from` is where Back goes**, so the patient overview does not have to know how it
 * was reached. Coming from a search hit has to return to the hits: a clinician who
 * searched a reservnummer, opened the wrong twin and landed on the ward list has lost
 * their search.
 *
 * The ward list stays the start screen (PHASE-0.md §2.2).
 */
type Screen =
  | { readonly kind: 'ward' }
  | { readonly kind: 'search' }
  /**
   * Registering a patient who is not in the record (DECISIONS.md #40).
   *
   * No `from`, because there is only one way in — the nav — and only one way out. It admits onto
   * the same selection the ward list shows, and since #47 it can also change it — one piece of
   * state, reached from either screen.
   */
  | { readonly kind: 'register' }
  | {
      readonly kind: 'patient'
      readonly patientId: string
      readonly from: 'ward' | 'search'
    }
  /**
   * A printed document for one patient (PHASE-0.md §2.6).
   *
   * Reached only from that patient's overview, and it carries `from` onwards so that Back
   * out of the print view returns to the overview and Back again still reaches the hits a
   * clinician searched. Printing is a detour, not a destination.
   */
  | {
      readonly kind: 'print'
      readonly patientId: string
      readonly documentKind: PrintDocument
      readonly from: 'ward' | 'search'
    }
  /** The audit checkpoint sheet (DECISIONS.md #53). Reached from the ward list; Back returns there. */
  | { readonly kind: 'checkpoint' }

/**
 * The catalogue key for each status, rather than the label itself.
 *
 * This was a `Record<ApiStatus, string>` of resolved labels, which was a latent bug
 * the moment a second catalogue existed: a module-scope `t()` runs once, when the
 * bundle is first evaluated, so the footer would have kept the language that was
 * selected then no matter what anybody chose afterwards. Keys are language-free, and
 * resolving happens in the render.
 */
/**
 * Whether the start page shows the audit checkpoint reminder — DECISIONS.md #55.
 *
 * **Off until there is somebody it is for.** Printing and filing a checkpoint sheet is one named
 * person's duty, not every clinician's: shown to everyone, it is a strip of jargon nobody owns. The
 * sheet, its endpoints, the `checkpoint` audit action and `--verify-audit` all stay; what waits is
 * the reminder, for the administrative role that comes with the interface for users, roles, wards
 * and beds. Then it is shown to that role rather than switched on here.
 */
const CHECKPOINT_REMINDER_SHOWN = false

const statusKey = {
  checking: 'status.checking',
  ready: 'status.ready',
  'alive-no-database': 'status.aliveNoDatabase',
  unreachable: 'status.unreachable',
} as const satisfies Record<ApiStatus, string>

async function probeApi(signal: AbortSignal): Promise<ApiStatus> {
  try {
    const ready = await fetch('/api/health/ready', { signal })
    if (ready.ok) {
      return 'ready'
    }

    const alive = await fetch('/api/health', { signal })
    return alive.ok ? 'alive-no-database' : 'unreachable'
  } catch {
    return 'unreachable'
  }
}

/**
 * The signed-in application.
 *
 * Only ever rendered inside `SessionGate`, which is why {@link Session} is a required prop
 * rather than something nullable: there is no state of this component in which nobody is
 * signed in, and a screen that had to handle one would be a second answer to a question the
 * gate already answers.
 *
 * @param session Who is signed in. The gate owns it; nothing here refetches it.
 * @param onSignOut Discards the session and returns to the login screen.
 */
export function App({
  session,
  onSignOut,
}: {
  readonly session: Session
  readonly onSignOut: () => void
}) {
  const { t } = useTranslation(['common', 'auth'])
  const [status, setStatus] = useState<ApiStatus>('checking')
  const [checkedAt, setCheckedAt] = useState<Date | null>(null)
  const [screen, setScreen] = useState<Screen>({ kind: 'ward' })

  // Held here rather than in the search screen so that opening a patient and pressing
  // Back returns to the hits — see `PatientSearchScreen`. Cleared by the nav buttons
  // below, which is the other half of the same decision.
  const [searchTerm, setSearchTerm] = useState('')

  // Which ward is selected — what the list shows, and since #47 also what the registration form
  // admits to. Held here for the same reason as the term above: opening a patient unmounts the
  // ward list, and a clinician who presses Back has to come back to the ward they left rather
  // than to whichever one the directory happens to answer with first.
  //
  // Null until something has asked the box which wards exist. Either control can be the one that
  // does: `WardPicker` on the ward list, `WardField` on the registration form — and on the path
  // where a clinician signs in and goes straight to Registrera, `WardField` is the only one, since
  // the two screens are never mounted together. Deliberately not seeded with a code — a default
  // compiled in here would be the second place that decides which ward phase 0 shows, which is
  // what deleting `PHASE_0_WARD` was about. Unlike the term, this is **not** cleared by the nav
  // buttons: a ward is where somebody works for a shift, not a query they ran once.
  const [ward, setWard] = useState<string | null>(null)

  const show = (next: Screen) => {
    if (next.kind === 'ward' || next.kind === 'search' || next.kind === 'register') {
      // Navigating away deliberately discards the term. Keeping it would mean the next
      // visit to the search screen re-ran somebody's last search on arrival, and every
      // search is an audit row that can never be deleted (AGENTS.md §3, §4).
      //
      // The patient and print screens are the two that keep it, because both are reached
      // *from* the hits and both have a Back that leads there. Registration is not one of
      // them even though it is usually reached *after* a fruitless search: it has no Back to
      // the hits, so keeping the term would only mean the next visit to the search screen
      // re-ran a search the clinician had already given up on.
      setSearchTerm('')
    }
    setScreen(next)
  }

  useEffect(() => {
    const controller = new AbortController()

    void probeApi(controller.signal).then((result) => {
      if (!controller.signal.aborted) {
        setStatus(result)
        setCheckedAt(new Date())
      }
    })

    return () => controller.abort()
  }, [])

  return (
    <>
      {/*
        The banner is permanent and unconditional. Staff must never be in doubt
        about which system they are documenting in — a note written here does not
        exist in the central EHR until it is handed back.
        See docs/PHASE-0.md §2.
      */}
      <header className="downtime-banner" role="banner">
        {t('app.banner')}
      </header>

      {/* On every screen of an internet demo host, and nowhere else — DECISIONS.md #49. */}
      <DeploymentNotice />
      <TranslationNotice />

      {/*
        The mark is decorative and aria-hidden, so the name beside it carries the
        whole accessible label. It sits here, under the banner and never inside it:
        the banner's colour is reserved (index.css) and a logo in it would compete
        with the one thing on screen that must not be missed.

        The wrapper around it exists only to carry the tint. `Mark` inherits
        `currentColor` so that one component serves light mode, dark mode and
        print, which means the colour has to come from CSS — and from something
        narrower than `.app-bar`, or the product name and the nav buttons would
        inherit it too. See `.app-bar__mark` in index.css.
      */}
      <div className="app-bar">
        <span className="app-bar__mark">
          <Mark size={20} />
        </span>
        <span className="app-bar__name">{t('app.name')}</span>

        {/*
          Three destinations, so three buttons and no menu to open. `aria-current` rather
          than colour alone carries which one is showing (WCAG 1.4.1, ARCHITECTURE.md §13).

          Registration is last, and it is in the nav rather than on the ward list because a
          patient who is not in the record is not on a bed yet — reaching it should not
          require guessing which screen owns them. It is offered to every role: the form
          itself withholds what a role may not use, and the refusal that counts is the
          server's (DECISIONS.md #39).
        */}
        <nav className="app-bar__nav" aria-label={t('app.name')}>
          <button
            type="button"
            aria-current={screen.kind === 'ward' ? 'page' : undefined}
            onClick={() => show({ kind: 'ward' })}
          >
            {t('nav.ward')}
          </button>
          <button
            type="button"
            aria-current={screen.kind === 'search' ? 'page' : undefined}
            onClick={() => show({ kind: 'search' })}
          >
            {t('nav.search')}
          </button>
          <button
            type="button"
            aria-current={screen.kind === 'register' ? 'page' : undefined}
            onClick={() => show({ kind: 'register' })}
          >
            {t('nav.register')}
          </button>
        </nav>

        {/*
          Who is documenting, on every screen.

          This is what phase 0 does instead of an idle lock: a shared ward terminal where the
          previous clinician's session is still open is how a note ends up signed by the wrong
          person, and the audit log's central claim — that actor_id is who did it — is then
          false. So the name is not tucked into a menu, and the way out is beside it.

          The role is the translated label from the code, not the stored Swedish title: this
          line is a reader identifying themselves rather than reading the record. See
          `signedInAs`.
        */}
        <span className="app-bar__clinician">{signedInAs(session, t)}</span>

        {/*
          The language selector is here *as well as* on the login screen, not instead of it
          (`DECISIONS.md` #44). `I18N.md` §6 requires it before anybody is signed in — a foreign
          medical team must not have to log in through a Swedish interface — and that is a
          requirement about the login screen, not a prohibition on this one. Signing out to change
          language is what a shared ward terminal cannot afford: the note and correction screens
          guard unsaved text precisely because it is the only copy, and sign-out discards it.
        */}
        <LanguageChoice />

        {/*
          Last in the bar and last in the tab order, because it is the control a clinician
          presses once at the end of a shift and the nav is what they use throughout it.
        */}
        <button type="button" className="app-bar__signout" onClick={onSignOut}>
          {t('auth:session.logout')}
        </button>
      </div>

      {/*
        Unmounted rather than hidden. A hidden ward list would keep polling, and a
        hidden patient overview would hold a record on screen that nobody is reading.
        The search term survives the trip into a patient because `App` holds it, not
        because the screen stayed mounted — which is the difference between coming
        back to your hits and repeating a search you did not ask for.
      */}
      {/* Above the ward list when it is shown — which it is not yet (DECISIONS.md #55). */}
      {CHECKPOINT_REMINDER_SHOWN && screen.kind === 'ward' && (
        <CheckpointReminder onPrint={() => show({ kind: 'checkpoint' })} />
      )}

      {screen.kind === 'checkpoint' && <CheckpointScreen onBack={() => show({ kind: 'ward' })} />}

      {screen.kind === 'ward' && (
        <WardListScreen
          ward={ward}
          onWardChange={setWard}
          onOpenPatient={(patientId) => show({ kind: 'patient', patientId, from: 'ward' })}
        />
      )}

      {screen.kind === 'search' && (
        <PatientSearchScreen
          term={searchTerm}
          onTermChange={setSearchTerm}
          onOpenPatient={(patientId) => show({ kind: 'patient', patientId, from: 'search' })}
        />
      )}

      {screen.kind === 'register' && (
        <PatientRegistrationScreen
          // The same selection the ward list shows, and since #47 the form can change it —
          // which is why both halves are passed. One piece of state, two controls onto it: a
          // second copy inside the form could disagree with the list, and a patient admitted
          // to a ward nobody chose cannot be moved back out (AGENTS.md §3).
          //
          // A consequence worth having: pressing Close after registering onto another ward
          // leaves the list on the ward the new patient is actually on.
          ward={ward}
          onWardChange={setWard}
          // For `canDocument`, on the same terms as the patient overview: a courtesy, never
          // the check. `AccessGate` refuses the write and audits the refusal.
          session={session}
          onOpenPatient={(patientId) => show({ kind: 'patient', patientId, from: 'ward' })}
          onClose={() => show({ kind: 'ward' })}
        />
      )}

      {screen.kind === 'patient' && (
        // Keyed by the patient, so opening a second patient from a search remounts
        // rather than showing the first one's safety panel while the second loads. On
        // this screen a stale allergy panel is the failure mode that matters.
        <PatientOverviewScreen
          key={screen.patientId}
          patientId={screen.patientId}
          // For `canDocument`, which decides whether the note form is offered at all — a
          // courtesy and never the check. The refusal that counts is server-side on the
          // write, where it is also audited (`AccessGate`). This is what stops a role that
          // may not document being offered a form that refuses them after they have typed a
          // note.
          session={session}
          onBack={() => setScreen({ kind: screen.from })}
          onPrint={(documentKind) =>
            show({
              kind: 'print',
              patientId: screen.patientId,
              documentKind,
              from: screen.from,
            })
          }
        />
      )}

      {screen.kind === 'print' && (
        // Keyed by patient *and* document, so asking for the wristband after the
        // signeringslista remounts and fetches — which is what makes the second request its
        // own audit row naming the second document (AGENTS.md §4).
        <PrintScreen
          key={`${screen.patientId}-${screen.documentKind}`}
          patientId={screen.patientId}
          documentKind={screen.documentKind}
          onBack={() =>
            show({ kind: 'patient', patientId: screen.patientId, from: screen.from })
          }
        />
      )}

      {/*
        Kept from the scaffold rather than dropped, and moved out of the way. The
        ward list reports its own failure as one thing — "unavailable" — but a
        technician at 03:00 needs to know whether the API is down or only the
        database, and that is the difference between restarting a container and
        reading the db logs (AGENTS.md §10).
      */}
      <footer className="app-footer">
        <span className={`status status--${status}`} />
        {t(statusKey[status])}
        {checkedAt !== null && <> ({formatClinicalDateTime(checkedAt)})</>}
      </footer>
    </>
  )
}
